import { Component, ElementRef, Injector, OnInit, ViewChild, inject, signal, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { PrimeNG } from 'primeng/config';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';

import { AppInfo, Config } from '@mitel/cloudlink-sdk';
import { AuthenticationService as CloudLinkAuth, UserClaims } from '@mitel/cloudlink-sdk/authentication';
import { AdminService } from '@mitel/cloudlink-sdk/admin';
import { MenuItem } from 'primeng/api';
import { MiMenuComponent, NavigationMenuItem } from '@mitel-internal/primeng-lib';
import { ChatCloudLinkService, version as chatLibVersion } from '@mitel-internal/cloudlink-components-chat';
import { version as appVersion } from '../../package.json';

import { LocalStorage } from 'src/app/classes/local-storage';

import { ConversationListService } from './services/conversation-list.service';
import { DevToolsService, HostAvatarMode } from './services/dev-tools.service';
import { AVAILABLE_LOCALES, LocaleService } from './services/locale.service';
import { ThemeService } from './services/theme.service';
import { Conversation } from './Conversation';

import { environment } from 'src/environments/environment';
import { createNotify } from '@mitel/cloudlink-sdk/notifications';

declare const Paho: any;

const HOST_AVATAR_MODE_LABELS: Record<HostAvatarMode, string> = { off: 'Off', test: 'Test', removed: 'Removed' };

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: false
})
export class AppComponent implements OnInit {
  title = 'app';
  // Displayed in the header, before the Dev tools button - the demo app and the library it
  // wraps version independently, so both are shown: this app's own version (root package.json)
  // and which library build it's actually running against.
  readonly appVersion = appVersion;
  readonly chatLibVersion = chatLibVersion;
  // Set once the service worker has downloaded a new version in the background - see
  // setupServiceWorkerUpdates(). Drives the "Update available" pill next to the version display.
  readonly updateAvailable = signal(false);
  private readonly auth: CloudLinkAuth;
  private readonly admin: AdminService;
  private appClientId: string = '3309fd73f7315c07d4ba2f9e4960fb78';
  claims: UserClaims;
  private sessionTokenKey = '@mitel-internal/cloudlink-components-chat/cl-chat-component-sample-token';
  private _deviceToken;

  // Constants
  private readonly MESSAGES_ROUTE = 'messages';
  private readonly OPTIONS_ROUTE = 'options';

  // Captured directly from window.location.hash at the very top of ngOnInit (see there) rather
  // than read off the Router/ActivatedRoute later - loadConversation() can fire from the async
  // checkAuth()/ngOnInit() chain before the Router has finished ingesting the real browser URL's
  // query params into its own state, so `queryParamsHandling: 'preserve'` alone would have nothing
  // to preserve and silently drop params like ?embed=true on that first navigation.
  private initialQueryParams: Record<string, string> = {};

  // Set once, from initialQueryParams, right after it's parsed in ngOnInit - mirrors
  // ChatPageComponent.isEmbedded, hiding this app's own nav sidebar/header chrome for iframe
  // embedding the same way that component hides mi-two-pane-layout's chrome.
  isEmbedded = false;

  private get messagesRootPath(): string {
    return `/${this.MESSAGES_ROUTE}`;
  }

  private isAtOptionsRoute(): boolean {
    return this.router.url.split('?')[0] === `/${this.OPTIONS_ROUTE}`;
  }

  // Helper method for route validation
  private isValidConversationRoute(): boolean {
    const currentRoute = this.router.url.split('?')[0];
    return (
      currentRoute !== '/' &&
      currentRoute !== this.messagesRootPath &&
      !this.isAtOptionsRoute() &&
      !!this.conversationId
    );
  }
  private get deviceToken(): string {
    if (!this._deviceToken) {
      const sessionToken = sessionStorage.getItem(this.sessionTokenKey);
      if (!!sessionToken) {
        this._deviceToken = sessionToken;
      } else {
        this._deviceToken = this.generateToken();
        sessionStorage.setItem(this.sessionTokenKey, this._deviceToken);
      }
    }
    return this._deviceToken;
  }

  conversations: Conversation[];

  @LocalStorage
  public conversationId;

  navCollapsed = signal<boolean>(false);

  navItems: NavigationMenuItem[] = [
    {
      label: 'Messages',
      icon: 'mi2-messages',
      iconFilled: 'mi2-messages-filled',
      routerLink: '/messages',
      routerLinkActiveOptions: { exact: false }
    },
    {
      label: 'Options',
      icon: 'mi2-settings',
      iconFilled: 'mi2-settings-filled',
      routerLink: '/options',
      routerLinkActiveOptions: { exact: true }
    }
  ];

  private primeng = inject(PrimeNG);

  @ViewChild('profileMenuBtn') private profileMenuBtnRef: ElementRef<HTMLButtonElement>;
  // mi-round-button is a component, not a plain <button> - read its host element instead of
  // the component instance so it can be used as mi-menu's positioning anchor.
  @ViewChild('devToolsBtn', { read: ElementRef }) private devToolsBtnRef: ElementRef<HTMLElement>;
  @ViewChild('languageMenu') private languageMenu: MiMenuComponent;
  @ViewChild('devToolsMenu') private devToolsMenu: MiMenuComponent;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public themeService: ThemeService,
    public localeService: LocaleService,
    public devToolsService: DevToolsService,
    private conversationListService: ConversationListService,
    // Not injected directly: ChatCloudLinkService's constructor eagerly calls checkForNotify(),
    // which captures SharedHelpers.getSdkConfig() once - constructing it here, as an AppComponent
    // constructor param, would resolve it before Config.global is set a few lines down, so it'd
    // capture an unconfigured SDK and checkForNotify() would fail permanently ("No notify
    // configured"). Injector defers the actual construction to logout()'s injector.get() call,
    // by which point Config.global has long since been set.
    private injector: Injector,
    private swUpdate: SwUpdate,
    private cdr: ChangeDetectorRef
  ) {
    // Configure CL SDK - use Config global for now
    Config.global = new Config({
      appInfo: new AppInfo({
        correlationIdPrefix: 'cl-chat-component-sample',
        app: {
          name: 'chat-component-sample',
          version: '0.0.1'
        },
        enableAppHeader: true
      }),
      cloud: this.getCloud(),
      stage: this.getStage()
    });

    this.auth = Config.global.getAuthenticationService();
    this.claims = { name: 'unknown' } as UserClaims;
    this.admin = new AdminService({ config: Config.global });
  }

  async ngOnInit() {
    const url = window.location.href;
    console.log('AppComponent initializing', url);

    this.setupServiceWorkerUpdates();

    // Read conversation ID from the current URL on app load (handles page refresh). The app uses
    // HashLocationStrategy, so the route lives in location.hash, not location.pathname (which is
    // always just "/"). When the URL has no id (e.g. a bare reload of "/"), leave the persisted
    // conversationId (@LocalStorage-backed, see below) alone instead of clearing it - that's the
    // "previous route" restoreConversation() below tries to return to.
    const [initialPath, initialQuery] = window.location.hash.replace(/^#\/?/, '').split('?');
    const initialSegments = initialPath.split('/');
    const initialId = initialSegments[0] === this.MESSAGES_ROUTE ? initialSegments[1] : undefined;
    if (initialId) {
      this.conversationId = initialId;
    }
    this.initialQueryParams = initialQuery ? Object.fromEntries(new URLSearchParams(initialQuery)) : {};
    this.isEmbedded = this.initialQueryParams['embed'] !== undefined && this.initialQueryParams['embed'] !== 'false';

    // aiMode's demo conversation talks directly to MAIS's own /v1 API (bearer API-key auth via
    // AiConfigService), not a CloudLink session - so when embedded specifically on that route,
    // skip the CloudLink SSO login flow entirely instead of redirecting the iframe out to Mitel
    // auth. 'ai-mode-demo' matches ChatPageComponent.AI_MODE_CONVERSATION_ID / conversation-list's
    // own check - not pulled into a shared constant, matching this codebase's existing convention
    // of duplicating that literal at each call site.
    if (this.isEmbedded && initialId === 'ai-mode-demo') {
      return;
    }

    // Track route changes to keep the stored conversation ID in sync. Only ever updates it when
    // the new route actually has one - landing on the bare "/messages" route (no id) happens
    // transiently during the redirect-then-restore flow above, and must not clear out the very
    // conversationId restoreConversation() is about to read back.
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      const segments = event.urlAfterRedirects.split('?')[0].replace(/^\//, '').split('/');
      const id = segments[0] === this.MESSAGES_ROUTE ? segments[1] : undefined;
      if (id) {
        this.conversationId = id;
      }
    });

    // Handle GAuth Callback - parse query parameters directly from URL
    const params = new URLSearchParams(url.split('?')[1]);
    const code = params.get('code');
    console.log('app.component.ngOnInit()', params, code);
    if (code) {
      console.log('app.component.ngOnInit()=>Mitel code callback', code);
      const res = await this.auth.login({
        code: { code: code, clientId: this.appClientId }
      });
      const token = res.body;
      await this.auth.setToken(token);
      const resClaims = await this.auth.whoAmI();
      const claims = resClaims.body as UserClaims;
      if (claims) {
        this.claims = claims;
        this.cdr.markForCheck();
        this.setListServiceValues();
        this.setupNotify();
        this.conversationListService.setUpSubscriptions();
        // Only load conversation if there's a valid route parameter
        if (this.isValidConversationRoute()) {
          this.loadConversation(this.conversationId);
        } else if (!this.isAtOptionsRoute()) {
          this.restoreConversation();
        }
      }
    } else {
      this.setupNotify();
      if (window.navigator.onLine) {
        this.login();
      } else {
        // When offline, don't automatically load stored conversation
        // Only load if there's a valid route parameter
        if (this.isValidConversationRoute()) {
          this.loadConversation(this.conversationId);
        }
      }
    }
  }

  async login(): Promise<void> {
    try {
      const resClaims = await this.auth.whoAmI();
      const claims = resClaims.body as UserClaims;
      if (claims) {
        this.claims = claims;
        this.cdr.markForCheck();
      }
    } catch (error) {
      console.warn('app.component.login()=> not loggedin', error);
    }

    if (!this.claims || this.claims.name === 'unknown') {
      // login via gAuth
      const redirectUrl = encodeURIComponent(window.location.href);
      const cloudPrefix: string = Config.global.cloud === 'dev' ? '.dev' : '';
      const mitelLogin = `https://auth${cloudPrefix}.mitel.io/authorize?app_name=CloudLinkChat&client_id=3309fd73f7315c07d4ba2f9e4960fb78&response_type=code&redirect_uri=${redirectUrl}`;
      console.log('Redirecting to Mitel auth with redirect_uri:', redirectUrl);
      window.location.href = mitelLogin;
    } else {
      console.log("app.component.checkAuth()=> you're logged in as...", this.claims);
      this.setListServiceValues();
      this.conversationListService.setUpSubscriptions();
      // Only load conversation if there's a valid route parameter
      if (this.isValidConversationRoute()) {
        console.log('app.component.checkAuth()=> loading conversation from route parameter:', this.conversationId);
        this.loadConversation(this.conversationId);
      } else if (!this.isAtOptionsRoute()) {
        this.restoreConversation();
      }
    }
  }

  setListServiceValues() {
    this.conversationListService.loggedInAccountId = this.claims.accountId;
    this.conversationListService.loggedInUserId = this.claims.principalId;
  }

  async logout(): Promise<void> {
    // Tell the server to stop pushing to this device before tearing down the client - a reload
    // alone drops the local websocket, but leaves the server-side notify subscriptions
    // (subscribeUsers/Participants/Messages/etc., see ChatCloudLinkService.subscribeToSubs())
    // registered against a device id that's now gone, until they eventually expire on their own.
    try {
      await this.injector.get(ChatCloudLinkService).unsubscribe();
    } catch (ex) {
      console.warn('app.component.logout()=> Error unsubscribing from notify', ex);
    }
    await this.auth.logout();
    this.claims = { name: 'unknown' } as UserClaims;
    window.location.reload();
  }

  get profileMenuItems(): MenuItem[] {
    const loggedIn = !!this.claims && this.claims.name !== 'unknown';
    const items: MenuItem[] = [];
    if (loggedIn) {
      items.push({ label: this.claims.name, disabled: true }, { separator: true });
    }
    // mi-menu unconditionally hides itself right after invoking an item's command (no way to
    // opt out), so a nested language list can't just swap this same menu's items in place -
    // that menu would immediately close again. Instead this opens a second, independent
    // mi-menu anchored to the same trigger button - the profile menu closes as normal.
    items.push({ id: 'languages', label: 'Languages', command: () => this.openLanguageMenu() });
    items.push({ separator: true });
    items.push(
      loggedIn
        ? { id: 'logout', label: 'Logout', command: () => this.logout() }
        : { id: 'login', label: 'Login', command: () => this.login() }
    );
    return items;
  }

  private openLanguageMenu(): void {
    this.languageMenu.toggle(this.profileMenuBtnRef.nativeElement);
  }

  get languageMenuItems(): MenuItem[] {
    return AVAILABLE_LOCALES.map(({ code, label }) => ({
      id: `lang-${code}`,
      label,
      styleClass: this.localeService.locale() === code ? 'font-semibold' : '',
      command: () => this.localeService.setLocale(code)
    }));
  }

  // Live, in-context dev toggles (Offline/Destroy/Preview/Toggle Suggestions/Host avatar/OneDrive token) for whatever
  // conversation is currently open in ChatPageComponent - see DevToolsService. Kept in this
  // demo app's own header rather than inside mi-chat's own header, since that component
  // belongs to the shared library, not this demo.
  openDevToolsMenu(): void {
    this.devToolsMenu.toggle(this.devToolsBtnRef.nativeElement);
  }

  get devToolsMenuItems(): MenuItem[] {
    return [
      {
        id: 'dev-offline',
        label: `Offline: ${this.devToolsService.hostOffline() ? 'On' : 'Off'}`,
        command: () => this.devToolsService.toggleHostOffline()
      },
      {
        id: 'dev-destroy',
        label: `Destroy: ${this.devToolsService.destroyed() ? 'On' : 'Off'}`,
        command: () => this.devToolsService.toggleDestroyed()
      },
      {
        id: 'dev-preview',
        label: `Preview: ${this.devToolsService.previewMode() ? 'On' : 'Off'}`,
        command: () => this.devToolsService.togglePreviewMode()
      },
      {
        id: 'dev-suggestions',
        label: `Suggestions: ${this.devToolsService.showingSuggestions() ? 'On' : 'Off'}`,
        command: () => this.devToolsService.toggleShowingSuggestions()
      },
      {
        id: 'dev-host-avatar',
        label: `Host avatar: ${HOST_AVATAR_MODE_LABELS[this.devToolsService.hostAvatarMode()]}`,
        command: () => this.devToolsService.cycleHostAvatarMode()
      },
      {
        id: 'dev-msgraph-token',
        label: `OneDrive token: ${this.devToolsService.msGraphTokenSet() ? 'Set' : 'Off'}`,
        command: () => this.devToolsService.promptMsGraphToken()
      }
    ];
  }

  private setupNotify() {
    console.log('app.component.setupNotify()=> initializing notifications with device ID:', this.deviceToken);
    const websocket = {
      deviceId: 'cl-chat-component-sample-' + this.deviceToken,
      pahoMqtt: (websocketUrl, clientId) => new Paho.Client(websocketUrl, clientId),
      autoReconnect: true
    };
    this.conversationListService.notify = createNotify({ config: Config.global, websocket });
  }

  // A new deploy's service worker only gets NOTICED on its own checks (app startup, or a route
  // navigation) - a tab left open for hours wouldn't otherwise hear about it, so poll
  // periodically too. VERSION_READY means the new version has already finished downloading in
  // the background and is safe to switch to; surface a dismissible pill (see updateAvailable)
  // rather than reloading out from under the user, since that could drop an unsent typed message.
  private setupServiceWorkerUpdates(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }
    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(() => {
        this.updateAvailable.set(true);
      });
    setInterval(
      () => {
        this.swUpdate.checkForUpdate().catch(ex => {
          console.warn('app.component.setupServiceWorkerUpdates()=> checkForUpdate failed', ex);
        });
      },
      30 * 60 * 1000
    );
  }

  public reloadForUpdate(): void {
    window.location.reload();
  }

  private generateToken() {
    return Math.floor(1000000000000000 + Math.random() * 9000000000000000)
      .toString(36)
      .substr(0, 10);
  }

  // Landed on "/messages" with no id (fresh load, or a persisted conversationId that no longer
  // resolves to anything - see below). Prefer returning the user to the conversation they
  // previously had open; only fall back to the first conversation in the Chat tab (matching
  // conversation-list's default 'direct' tab) if that previous one isn't there any more.
  private async restoreConversation() {
    await new Promise<void>(resolve => {
      this.conversationListService.ready$.pipe(take(1)).subscribe(() => resolve());
    });
    const previousConversationId = this.conversationId;
    try {
      const result = await this.conversationListService.getConversations({ filter: 'stream eq false' });
      const conversations = result[0] as Conversation[];
      if (previousConversationId && conversations?.some(c => c.id === previousConversationId)) {
        this.loadConversation(previousConversationId);
        return;
      }
      if (conversations?.length > 0) {
        this.loadConversation(conversations[0].id);
        return;
      }
      const streamResult = await this.conversationListService.getConversations({ filter: 'stream eq true' });
      const streamConversations = streamResult[0] as Conversation[];
      if (previousConversationId && streamConversations?.some(c => c.id === previousConversationId)) {
        this.loadConversation(previousConversationId);
        return;
      }
      if (streamConversations?.length > 0) {
        this.loadConversation(streamConversations[0].id);
      }
    } catch (e) {
      console.warn('app.component.restoreConversation() failed', e);
    }
  }

  public loadConversation(convoId) {
    // Strip the query string before comparing - router.url includes it (e.g.
    // "/messages/ai-mode-demo?embed=true"), so comparing it as-is against a bare path never
    // matches whenever there's a query string, causing an unconditional (and query-param-dropping)
    // re-navigation on every load/login even when already on the right conversation.
    const currentRoute = this.router.url.split('?')[0];

    if (convoId) {
      // Don't navigate if we're already on the correct route
      if (currentRoute !== `/${this.MESSAGES_ROUTE}/${convoId}`) {
        this.router.navigate(['/', this.MESSAGES_ROUTE, convoId], {
          queryParams: this.initialQueryParams,
          queryParamsHandling: 'merge'
        });
      }
    } else {
      // No conversation to show (e.g. the account has none at all) - stay on the bare
      // messages route rather than a dedicated "empty" one.
      if (currentRoute !== this.messagesRootPath) {
        this.router.navigate(['/', this.MESSAGES_ROUTE], {
          replaceUrl: true,
          queryParams: this.initialQueryParams,
          queryParamsHandling: 'merge'
        });
      }
    }
  }

  public getCloud(): string {
    let cloud = 'public';
    if (
      !environment.production ||
      window.location.href.indexOf('.dev.') > 0 ||
      window.location.href.indexOf('-dev.mitel.io') > 0 ||
      window.location.href.indexOf('localhost') > 0 ||
      window.location.href.indexOf('ngrok') > 0 ||
      window.location.href.indexOf('127.0.0.1') > 0
    ) {
      cloud = 'dev';
    }

    return cloud;
  }

  public getStage() {
    if (window.location.href.indexOf('forcestage=true') > 0) {
      return 'stage';
    } else {
      return 'live';
    }
  }
}
